// Reusable hook for async analysis: submit -> poll -> result lifecycle
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  asyncAnalysisService,
  type AnalysisComponent,
  type AnalyzeAsyncResponse,
} from '../services/async-analysis.service';
import type { AnalysisSession } from '../types/analysis-session';
import { ApiError } from '../services/http.client';
import { SubscriptionService, ANALYSIS_COSTS } from '../services/subscription.service';
import { checkMediaDuration } from '../utils/file-validation';

interface AsyncAnalysisState {
  isAnalyzing: boolean;
  sessionId: string | null;
  session: AnalysisSession | null;        // progressive partial session from polling
  result: AnalysisSession | null;         // final complete session
  error: string | null;
  warnings: string[];
  progress: number;
  statusText: string;
  skipped: boolean;                       // URL extraction skipped gracefully
  skipMessage: string | null;             // human-readable skip reason
}

const INITIAL_STATE: AsyncAnalysisState = {
  isAnalyzing: false,
  sessionId: null,
  session: null,
  result: null,
  error: null,
  warnings: [],
  progress: 0,
  statusText: '',
  skipped: false,
  skipMessage: null,
};

const POLL_INTERVAL = 2500; // 2.5s
const MAX_POLL_TIME = 600000; // 10 min

/** Extract the most useful error message from an error */
function extractErrorMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    // Use the backend's human-readable error message directly
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return t('status.submitFailed');
}

/** Check if user has enough credits for the analysis type. Returns error string or null. */
async function checkCredits(analysisType: string, t: (key: string, opts?: Record<string, any>) => string): Promise<string | null> {
  try {
    const usage = await SubscriptionService.getUsageStats();
    const cost = ANALYSIS_COSTS[analysisType] || 1;
    if (usage.credits.remaining < cost) {
      return t('status.insufficientCredits', { cost, remaining: usage.credits.remaining });
    }
    return null;
  } catch {
    // If credit check fails, let the backend enforce limits
    return null;
  }
}

export function useAsyncAnalysis(component: AnalysisComponent) {
  const { t } = useTranslation();
  const [state, setState] = useState<AsyncAnalysisState>({ ...INITIAL_STATE });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pollFailCountRef = useRef<number>(0);
  const submittingRef = useRef(false); // guards against double-submit

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (sessionId: string) => {
      stopPolling();
      startTimeRef.current = Date.now();
      pollFailCountRef.current = 0;

      pollRef.current = setInterval(async () => {
        // Timeout check
        if (Date.now() - startTimeRef.current > MAX_POLL_TIME) {
          stopPolling();
          setState((s) => ({
            ...s,
            isAnalyzing: false,
            error: t('status.timedOut'),
          }));
          return;
        }
        try {
          // getQueueStatus now returns AnalysisSession with _queue metadata
          const session = await asyncAnalysisService.getQueueStatus(sessionId);
          pollFailCountRef.current = 0; // reset on success

          const queue = session._queue;

          setState((s) => {
            let statusText = s.statusText;

            if (session.status === 'running' && queue) {
              const completed = queue.completed_components?.length ?? 0;
              const total = queue.total_components ?? 0;
              const isMedia = ['video', 'audio', 'image'].includes(session.input_type ?? '');

              if (isMedia && completed === 0) {
                statusText = t('status.processingMedia');
              } else if (completed > 0 && completed === total - 1) {
                statusText = t('status.generatingVerdict');
              } else if (completed > 0 && total > 0) {
                statusText = t('status.processingProgress', { completed, total });
              } else {
                statusText = t('status.processing');
              }
            } else if (session.status === 'running') {
              statusText = t('status.processing');
            }

            return {
              ...s,
              session,
              progress: queue?.progress ?? s.progress,
              statusText,
            };
          });

          if (session.status === 'completed') {
            stopPolling();
            try {
              const rawResult = await asyncAnalysisService.getResult(sessionId);
              const result = rawResult.data;
              // Extract warnings from response
              const warnings: string[] = (result as any)?.warnings || [];
              setState((s) => ({
                ...s,
                isAnalyzing: false,
                result,
                session: result,
                warnings,
                progress: 100,
                statusText: t('common.complete'),
              }));
            } catch (resultErr) {
              console.error('[AsyncAnalysis] Failed to fetch result for completed session:', resultErr);
              setState((s) => ({
                ...s,
                isAnalyzing: false,
                error: t('status.completedNoResults'),
              }));
            }
          } else if (session.status === 'failed') {
            stopPolling();
            setState((s) => ({
              ...s,
              isAnalyzing: false,
              error: t('status.analysisFailed'),
              progress: 0,
            }));
          }
        } catch (err) {
          pollFailCountRef.current += 1;
          const count = pollFailCountRef.current;
          const isApi = err instanceof ApiError;
          console.warn(`[AsyncAnalysis] Poll error (${count}/5):`, isApi ? `HTTP ${(err as ApiError).status}` : err);

          // Stop immediately on 404 (session gone) or after 5 consecutive failures
          if ((isApi && (err as ApiError).status === 404) || count >= 5) {
            stopPolling();
            const msg = isApi && (err as ApiError).status === 404
              ? t('status.sessionNotFound')
              : t('status.lostConnection');
            setState((s) => ({
              ...s,
              isAnalyzing: false,
              error: msg,
            }));
          }
        }
      }, POLL_INTERVAL);
    },
    [stopPolling]
  );

  /** Handle response from any submit call -- poll if async, resolve if sync */
  const handleSubmitResponse = useCallback(
    (res: AnalyzeAsyncResponse) => {
      const sessionId = res.data.session_id;
      const warnings: string[] = res.warnings || [];

      // Graceful skip — URL extraction failed (TikTok private, no audio, etc.)
      if (res.data.skipped) {
        const userMsg = (res as any).user_message || res.data.message || res.message || t('status.urlExtractionFailed');
        const suggestion = (res as any).suggestion;
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          skipped: true,
          skipMessage: suggestion ? `${userMsg}\n${suggestion}` : userMsg,
          warnings,
        }));
        return;
      }

      if (res.async === false) {
        // Sync fallback -- data IS the full AnalysisSession
        const result = res.data as unknown as AnalysisSession;
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          sessionId,
          result,
          session: result,
          warnings,
          progress: 100,
          statusText: t('common.complete'),
        }));
      } else {
        // Async -- start polling
        setState((s) => ({
          ...s,
          sessionId,
          statusText: t('common.queued'),
          progress: 10,
        }));
        startPolling(sessionId);
      }
    },
    [startPolling]
  );

  const submitText = useCallback(
    async (text: string, extras?: Record<string, any>) => {
      if (submittingRef.current) return; // prevent double-submit
      submittingRef.current = true;
      // Lock button immediately to prevent double-click
      setState(s => ({ ...s, isAnalyzing: true, error: null }));
      try {
      const mediaType = extras?.media_type || 'text';
      const creditErr = await checkCredits(mediaType, t);
      if (creditErr) {
        setState({ ...INITIAL_STATE, error: creditErr });
        return;
      }
      setState({
        isAnalyzing: true,
        sessionId: null,
        session: null,
        result: null,
        error: null,
        warnings: [],
        progress: 5,
        statusText: t('common.submitting'),
        skipped: false,
        skipMessage: null,
      });
      try {
        const res = await asyncAnalysisService.submitText(component, text, extras);
        handleSubmitResponse(res);
      } catch (err) {
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          error: extractErrorMessage(err, t),
        }));
      }
      } finally { submittingRef.current = false; }
    },
    [component, handleSubmitResponse]
  );

  const submitUrl = useCallback(
    async (url: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      // Lock button immediately to prevent double-click
      setState(s => ({ ...s, isAnalyzing: true, error: null }));
      try {
      const creditErr = await checkCredits('url', t);
      if (creditErr) {
        setState({ ...INITIAL_STATE, error: creditErr });
        return;
      }
      setState({
        isAnalyzing: true,
        sessionId: null,
        session: null,
        result: null,
        error: null,
        warnings: [],
        progress: 5,
        statusText: t('common.submitting'),
        skipped: false,
        skipMessage: null,
      });
      try {
        const res = await asyncAnalysisService.submitUrl(component, url);
        handleSubmitResponse(res);
      } catch (err) {
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          error: extractErrorMessage(err, t),
        }));
      }
      } finally { submittingRef.current = false; }
    },
    [component, handleSubmitResponse]
  );

  const submitMedia = useCallback(
    async (file: File, mediaType: string, contextText?: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      // Lock button immediately to prevent double-click
      setState(s => ({ ...s, isAnalyzing: true, error: null }));
      try {
      // Check duration before anything else
      let durationSec = 0;
      try {
        durationSec = await checkMediaDuration(file);
      } catch (durErr) {
        setState({ ...INITIAL_STATE, error: durErr instanceof Error ? durErr.message : t('status.fileTooLong') });
        return;
      }
      const creditErr = await checkCredits(mediaType, t);
      if (creditErr) {
        setState({ ...INITIAL_STATE, error: creditErr });
        return;
      }
      setState({
        isAnalyzing: true,
        sessionId: null,
        session: null,
        result: null,
        error: null,
        warnings: [],
        progress: 3,
        statusText: t('common.uploading'),
        skipped: false,
        skipMessage: null,
      });
      try {
        const res = await asyncAnalysisService.submitMedia(
          component,
          file,
          mediaType,
          contextText,
          durationSec > 0 ? durationSec : undefined
        );
        handleSubmitResponse(res);
      } catch (err) {
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          error: extractErrorMessage(err, t),
        }));
      }
      } finally { submittingRef.current = false; }
    },
    [component, handleSubmitResponse]
  );

  const submitDomain = useCallback(
    async (input: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      // Lock button immediately to prevent double-click
      setState(s => ({ ...s, isAnalyzing: true, error: null }));
      try {
      const creditErr = await checkCredits('url', t);
      if (creditErr) {
        setState({ ...INITIAL_STATE, error: creditErr });
        return;
      }
      setState({
        isAnalyzing: true,
        sessionId: null,
        session: null,
        result: null,
        error: null,
        warnings: [],
        progress: 5,
        statusText: t('common.submitting'),
        skipped: false,
        skipMessage: null,
      });
      try {
        const res = await asyncAnalysisService.submitDomain(input);
        handleSubmitResponse(res);
      } catch (err) {
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          error: extractErrorMessage(err, t),
        }));
      }
      } finally { submittingRef.current = false; }
    },
    [handleSubmitResponse]
  );

  const reset = useCallback(() => {
    stopPolling();
    setState({ ...INITIAL_STATE });
  }, [stopPolling]);

  return {
    ...state,
    submitText,
    submitUrl,
    submitMedia,
    submitDomain,
    reset,
  };
}
